import * as Config from "config";
import { ArrayChip, BoolChip, NumericChip, RangeChip, TextChip } from "marketplace/_views/filters/filter-chip";
import { FilterChipType } from "marketplace/_views/filters/filter-chips";
import {
  EntityTypeInclusionFilterName,
  EntityTypeInclusionFilterNames,
  isEntityTypeInclusionFilterName,
} from "marketplace/search/entity-type-inclusion-filters";

export { EntityTypeInclusionFilterNames, isEntityTypeInclusionFilterName };
export type { EntityTypeInclusionFilterName };

/**
 * Marketplace search filters.
 *
 * Most `filterName` values here are also fields on MarketplaceSearchRequestSchema and on the API's
 * MarketplaceSearchQuery. The entity-type inclusion controls are the exception: the frontend maps
 * each disabled `include*` toggle onto the API's `hide*` transport fields at the request boundary.
 */

function createEntityTypeInclusionFilter(filterName: EntityTypeInclusionFilterName, title: string): BoolChip {
  const filter = new BoolChip(filterName, title, true);
  filter.updateValue(true);
  return filter;
}

export const Filters: Array<FilterChipType> = [
  Object.assign(
    new TextChip('query', 'Search'),
    {
      enabled: false,
      hidden: true
    } as TextChip
  ),
  createEntityTypeInclusionFilter('includeCompanies', 'Companies'),
  createEntityTypeInclusionFilter('includeTeams', 'Teams'),
  createEntityTypeInclusionFilter('includeIndividuals', 'Individuals'),
  Object.assign(
    new TextChip('city', 'City'),
    {
      enabled: false
    } as TextChip
  ),
  Object.assign(
    new ArrayChip('state', 'State'),
    {
      enabled: false,
      freeSolo: false,
      multiple: false,
      options: Config.states.map((option) => option.name)
    } as ArrayChip
  ),
  Object.assign(
    // Years since bar admission. The profile stores the admission year; the API converts.
    new NumericChip('yearsExperience', 'Years Experience'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    new ArrayChip('fluentLanguages', 'Fluent Languages'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.languages.map((option) => option.name)
    } as ArrayChip
  ),
  Object.assign(
    new ArrayChip('technicalLanguages', 'Technical Languages'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.languages.map((option) => option.name)
    } as ArrayChip
  ),
  Object.assign(
    new ArrayChip('regionsLicensed', 'States Licensed In'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.states.map((option) => option.name)
    } as ArrayChip
  ),
  Object.assign(
    new ArrayChip('regionsRegistered', 'Regions Registered In'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.countries.map((option) => option.name)
    } as ArrayChip
  ),
  Object.assign(
    new NumericChip('applicationsDrafted', 'Applications Drafted'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    new NumericChip('allowedApplications', 'Allowed Applications'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    new TextChip('school', 'Undergrad/Law School'),
    {
      enabled: false
    } as TextChip
  ),
  Object.assign(
    new TextChip('degree', 'Degree'),
    {
      enabled: false
    } as TextChip
  ),
  Object.assign(
    new TextChip('professionalRegistration', 'Professional Registration #'),
    {
      enabled: false
    } as TextChip
  ),
  Object.assign(
    new NumericChip('insuredAmount', 'Minimum Insurance'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    // A rate bound is meaningless without it: $500 an hour and $500 a project are not the same
    // requirement, and the parser had nowhere to put the unit so it dropped it.
    new ArrayChip('billingUnit', 'Billing Unit'),
    {
      enabled: false,
      freeSolo: false,
      multiple: false,
      options: ['hour', 'day', 'project', 'matter']
    } as ArrayChip
  ),
  Object.assign(
    // Reads "established no later than", so a lower year means an older firm. That is the existing
    // query contract's direction, not a new choice.
    new NumericChip('yearEstablished', 'Established No Later Than'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    new NumericChip('numberOfEmployees', 'Minimum Employees'),
    {
      enabled: false,
      default: 0
    } as NumericChip
  ),
  Object.assign(
    // A list, and offered from a fixed vocabulary, because the free-text version made the wording
    // decisive: "Electrical" and "Electrical Engineering" are one discipline to a reader and were
    // two different substring searches. The API resolves the two against each other for profiles
    // written before the vocabulary existed.
    new ArrayChip('technicalBackgrounds', 'Technical Background'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.technicalBackgrounds
    } as ArrayChip
  ),
  Object.assign(
    // What someone does, as distinct from what they trained in. Until this field existed a search
    // for "trademark dispute" had nothing to bind to and returned every attorney in the marketplace.
    new ArrayChip('practiceAreas', 'Practice Area'),
    {
      enabled: false,
      freeSolo: false,
      multiple: true,
      options: Config.practiceAreas
    } as ArrayChip
  ),
  Object.assign(
    new BoolChip('collaborates', 'Willing To Collaborate'),
    {
      enabled: false
    } as BoolChip
  ),
  Object.assign(
    new BoolChip('isOnCall', 'On Call'),
    {
      enabled: false
    } as BoolChip
  ),
  Object.assign(
    // Bound to the parsed numeric amount on the profile, not the stored rate text.
    new RangeChip('billingRate', 'Billing Rate'),
    {
      enabled: false,
      default: [0, 999],
      value: [0, 999],
      min: 0,
      max: 999,
      steps: 1
    } as RangeChip
  ),
  Object.assign(
    // Self-identified on the profile. Applied when the searcher asks for it - by this control or by
    // saying so in a natural-language query - but never inferred when they did not, and never a
    // ranking signal. See docs/software-design/marketplace-ai-search.md.
    new BoolChip('potentialDiversityCandidate', 'Diversity Candidate'),
    {
      enabled: false
    } as BoolChip
  )
];

// Team, leader, and member pickers already constrain the result type. Entity-type controls would
// be redundant there and are not part of those mode-specific requests.
export const SelectionFilters = Filters.filter(
  (filter) => !isEntityTypeInclusionFilterName(filter.filterName),
);
